import { useEffect, useState, useCallback } from 'react';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import {
  Mail, RefreshCw, Search, ArrowLeft, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, Clock, AlertTriangle,
} from 'lucide-react';

const STATUS_STYLES = {
  delivered:  { cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200', icon: CheckCircle2, label: 'Delivered' },
  accepted:   { cls: 'bg-sky-50 text-sky-700 ring-sky-200',              icon: Clock,        label: 'Accepted' },
  bounced:    { cls: 'bg-red-50 text-red-700 ring-red-200',              icon: XCircle,      label: 'Bounced' },
  complained: { cls: 'bg-orange-50 text-orange-700 ring-orange-200',     icon: AlertTriangle, label: 'Complained' },
  rejected:   { cls: 'bg-red-50 text-red-700 ring-red-200',              icon: XCircle,      label: 'Rejected' },
  disabled:   { cls: 'bg-navy/5 text-navy/60 ring-navy/10',              icon: XCircle,      label: 'Disabled' },
  no_key:     { cls: 'bg-amber-50 text-amber-700 ring-amber-200',        icon: AlertTriangle, label: 'No key' },
};

function StatusPill({ status }) {
  const s = STATUS_STYLES[status] || { cls: 'bg-navy/5 text-navy/70 ring-navy/10', icon: Clock, label: status };
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${s.cls}`} data-testid={`email-status-${status}`}>
      <Icon size={11}/> {s.label}
    </span>
  );
}

function fmt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString(undefined, { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit', second:'2-digit' });
}

const KINDS = [
  'welcome_member', 'welcome_supporter', 'verify_email', 'password_reset',
  'password_changed', 'security_alert', 'new_device', '2fa_enabled', '2fa_disabled',
  'email_changed_old', 'email_changed_new',
  'subscription_activated_member', 'subscription_activated_supporter',
  'subscription_canceled', 'payment_failed', 'winback',
];

export default function AdminEmailLog() {
  const { user } = useAuth();
  const [status, setStatus] = useState('');
  const [kind, setKind] = useState('');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [counters, setCounters] = useState({});
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (kind) params.set('kind', kind);
      if (q) params.set('q', q);
      params.set('limit', String(limit));
      params.set('skip', String(skip));
      const r = await http.get(`/admin/email-log?${params.toString()}`);
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
      setCounters(r.data?.counters || {});
    } catch (e) {
      toast.error('Failed to load email log');
    } finally { setLoading(false); }
  }, [status, kind, q, skip]);

  useEffect(() => { load(); }, [load]);

  const refresh = async (rid) => {
    if (!rid) return toast.error('This event was never accepted by Resend — nothing to refresh.');
    try {
      const r = await http.post(`/admin/email-log/${rid}/refresh`);
      toast.success(`Status: ${r.data?.status} (Resend: ${r.data?.last_event})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Refresh failed');
    }
  };

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success('Copied');
    } catch { toast.error('Copy failed'); }
  };

  if (!user?.is_admin) return <Navigate to="/app" replace />;

  return (
    <div className="min-h-screen bg-[#F9F8F6] p-5 lg:p-10 space-y-6" data-testid="admin-email-log-page">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy transition-smooth" data-testid="back-to-admin">
        <ArrowLeft size={14}/> Back to Admin
      </Link>

      <div className="flex items-center gap-3">
        <Mail className="text-gold" size={22}/>
        <div>
          <h1 className="font-heading text-3xl">Email delivery log</h1>
          <p className="text-sm text-navy/55">Every outbound Brotherhood+ email, with Resend delivery status.</p>
        </div>
      </div>

      {/* Status counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {['delivered','accepted','bounced','complained','rejected','disabled','no_key'].map(s => (
          <button
            key={s}
            onClick={() => { setStatus(status === s ? '' : s); setSkip(0); }}
            className={`rounded-xl p-3 text-left ring-1 transition-smooth ${status === s ? 'ring-navy bg-white shadow-sm' : 'ring-navy/10 bg-white/60 hover:bg-white'}`}
            data-testid={`counter-${s}`}
          >
            <StatusPill status={s}/>
            <p className="mt-2 font-heading text-2xl">{counters[s] || 0}</p>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 card-soft flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[10px] uppercase tracking-widest text-navy/50 mb-1">Search</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40"/>
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); setSkip(0); }}
              placeholder="recipient, subject, or Resend ID"
              className="w-full pl-9 pr-3 py-2 rounded-lg ring-1 ring-navy/10 focus:ring-navy outline-none text-sm"
              data-testid="email-log-search"
            />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-navy/50 mb-1">Kind</label>
          <select
            value={kind}
            onChange={(e) => { setKind(e.target.value); setSkip(0); }}
            className="py-2 px-3 rounded-lg ring-1 ring-navy/10 focus:ring-navy outline-none text-sm bg-white"
            data-testid="email-log-kind"
          >
            <option value="">All kinds</option>
            {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <button onClick={load} className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-medium hover:bg-navy/90 transition-smooth flex items-center gap-2" data-testid="email-log-refresh">
          <RefreshCw size={14}/> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl overflow-hidden card-soft">
        <table className="w-full text-sm">
          <thead className="bg-navy/[0.03] text-[11px] uppercase tracking-widest text-navy/55">
            <tr>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Recipient</th>
              <th className="text-left px-4 py-3">Subject / Kind</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Resend ID</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row, idx) => (
              <>
                <tr key={row.resend_id || `noid-${idx}`} className="border-t border-navy/5 hover:bg-navy/[0.02]" data-testid={`email-log-row-${idx}`}>
                  <td className="px-4 py-3 text-[12px] text-navy/70 whitespace-nowrap">{fmt(row.created_at)}<br/><span className="text-navy/40 text-[10px]">{row.response_ms != null ? `${row.response_ms}ms` : ''}</span></td>
                  <td className="px-4 py-3 text-[13px]">{row.recipient}</td>
                  <td className="px-4 py-3 text-[13px]">
                    <div className="line-clamp-1">{row.subject}</div>
                    <div className="text-[10px] text-navy/45 uppercase tracking-wider mt-0.5">{row.kind}</div>
                  </td>
                  <td className="px-4 py-3"><StatusPill status={row.status}/></td>
                  <td className="px-4 py-3">
                    {row.resend_id ? (
                      <button onClick={() => copy(row.resend_id)} className="font-mono text-[10px] text-navy/70 hover:text-navy transition-smooth" data-testid={`copy-resend-id-${idx}`}>
                        {row.resend_id.slice(0, 8)}…
                      </button>
                    ) : (
                      <span className="text-[10px] text-navy/30">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {row.resend_id && !['delivered','bounced','complained'].includes(row.status) && (
                      <button onClick={() => refresh(row.resend_id)} className="text-[11px] text-navy/70 hover:text-navy hover:underline mr-3" data-testid={`refresh-btn-${idx}`}>
                        Refresh
                      </button>
                    )}
                    <button onClick={() => setExpanded(expanded === idx ? null : idx)} className="text-[11px] text-navy/70 hover:text-navy hover:underline" data-testid={`expand-btn-${idx}`}>
                      {expanded === idx ? 'Hide' : 'Details'}
                    </button>
                  </td>
                </tr>
                {expanded === idx && (
                  <tr className="bg-navy/[0.02] border-t border-navy/5">
                    <td colSpan={6} className="px-4 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-[12px]">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-navy/45 mb-1">Sender</div>
                          <div className="font-mono">{row.sender}</div>
                          <div className="text-[10px] uppercase tracking-widest text-navy/45 mt-3 mb-1">Category</div>
                          <div>{row.category || '—'}</div>
                          <div className="text-[10px] uppercase tracking-widest text-navy/45 mt-3 mb-1">Resend ID</div>
                          <div className="font-mono text-[11px]">{row.resend_id || '—'}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-navy/45 mb-1">Error / SMTP</div>
                          <pre className="text-[11px] whitespace-pre-wrap font-mono bg-white ring-1 ring-navy/10 rounded p-3 max-h-40 overflow-auto">{row.error || '(none)'}</pre>
                          <div className="text-[10px] uppercase tracking-widest text-navy/45 mt-3 mb-1">Raw Resend response</div>
                          <pre className="text-[11px] whitespace-pre-wrap font-mono bg-white ring-1 ring-navy/10 rounded p-3 max-h-40 overflow-auto">{JSON.stringify(row.raw_response || {}, null, 2)}</pre>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-navy/45 text-sm">No email events match these filters.</td></tr>
            )}
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-navy/45 text-sm">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between text-[12px] text-navy/60">
        <div>{total.toLocaleString()} total · showing {items.length === 0 ? 0 : skip + 1}–{skip + items.length}</div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSkip(Math.max(0, skip - limit))}
            disabled={skip === 0}
            className="px-3 py-1.5 rounded-lg ring-1 ring-navy/10 hover:bg-white disabled:opacity-40 flex items-center gap-1"
            data-testid="email-log-prev"
          ><ChevronLeft size={14}/> Prev</button>
          <button
            onClick={() => setSkip(skip + limit)}
            disabled={skip + items.length >= total}
            className="px-3 py-1.5 rounded-lg ring-1 ring-navy/10 hover:bg-white disabled:opacity-40 flex items-center gap-1"
            data-testid="email-log-next"
          >Next <ChevronRight size={14}/></button>
        </div>
      </div>
    </div>
  );
}
